import { createHash, randomBytes } from 'crypto';
import * as localForage from 'localforage';

import { Module } from '../Module';

export interface IPKCECodeChallenge {
  codeChallenge: string;
  codeChallengeMethod: string;
}

/**
 * Refer to RFC 7636: Proof Key for Code Exchange by OAuth Public Clients
 * https://tools.ietf.org/html/rfc7636
 */
export class PKCEService {

  private static CODE_CHALLENGE_METHOD = 'S256'; // RFC 7636, section 4.2
  private static CODE_VERIFIER_STORAGE_KEY = 'PKCE.codeVerifier';
  private static HASH_ALGORITHM = 'sha256';
  private static NUM_RANDOM_BYTES = 32; // RFC 7636, section 7.1

  private readonly localForage = localForage.createInstance({ storeName: 'vizportal' });

  /**
   * Generates and stores codeVerifier, computes codeChallenge and returns an object with code challenge data
   */
  public generateCodeChallenge(): IPKCECodeChallenge {
    const codeVerifier = this.secureRandomValue();
    const codeChallenge = this.hash(codeVerifier);

    this.setLocalStorage(
      PKCEService.CODE_VERIFIER_STORAGE_KEY,
      this.encode(codeVerifier));

    return {
      codeChallenge: this.encode(codeChallenge),
      codeChallengeMethod: PKCEService.CODE_CHALLENGE_METHOD
    };
  }

  /**
   * Retrieves codeVerifier that was created and stored in generateCodeChallenge
   */
  public getStoredCodeVerifier(): Promise<string> {
    return this.getLocalStorage(PKCEService.CODE_VERIFIER_STORAGE_KEY);
  }

  /**
   * URL-safe Base64 encoding
   */
  private encode(value: Buffer): string {
    return value.toString('base64')
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  public setLocalStorage(key: string, val: any): Promise<any> {
    return this.localForage.setItem(key, val);
  }

  public getLocalStorage(key: string): Promise<any> {
    return this.localForage.getItem(key);
  }

  private secureRandomValue(): Buffer {
    return randomBytes(PKCEService.NUM_RANDOM_BYTES);
  }

  private hash(value: Buffer): Buffer {
    return createHash(PKCEService.HASH_ALGORITHM).update(value).digest();
  }

}

Module.instance().service('PKCEService', PKCEService);
